import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
    ChevronDown,
    ChevronRight,
    BookOpen,
    PlayCircle
} from 'lucide-react'
import { contentApi } from '@/api/content'
import { CourseContent } from './CourseContent'
import classNames from 'classnames'

interface CurriculumSidebarProps {
    curriculumId: number
    activeCourseId?: number
    activeLessonId?: number
    className?: string
}

export function CurriculumSidebar({ curriculumId, activeCourseId, activeLessonId, className }: CurriculumSidebarProps) {
    const navigate = useNavigate()
    const { data: curriculum, isLoading } = useQuery({
        queryKey: ['curriculum', curriculumId],
        queryFn: () => contentApi.getCurriculumDetail(curriculumId),
        enabled: !!curriculumId,
        staleTime: 10 * 60 * 1000,
    })

    if (isLoading) {
        return (
            <div className={classNames("w-80 bg-gray-50 border-r border-gray-200 h-full p-4", className)}>
                <div className="h-6 bg-gray-200 rounded-sm w-3/4 mb-6 animate-pulse" />
                <div className="space-y-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="space-y-2">
                            <div className="h-10 bg-gray-200 rounded-sm animate-pulse" />
                            <div className="h-6 bg-gray-200 rounded-sm w-1/2 ml-4 animate-pulse" />
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    if (!curriculum) return null

    return (
        <div className={classNames("w-80 bg-gray-50 border-r border-gray-200 h-full flex flex-col", className)}>
            {/* Curriculum Header */}
            <div className="p-5 border-b border-gray-200 bg-white">
                <h2 className="font-bold text-gray-900 leading-tight">
                    {curriculum.title}
                </h2>
                <div className="mt-2 text-xs text-gray-500 font-medium tracking-wide">
                    CURRICULUM
                </div>
            </div>

            {/* Courses List */}
            <div className="flex-1 overflow-y-auto custom-scrollbar">
                <div className="py-2">
                    {curriculum.courses.map((course) => {
                        const isActive = course.id === activeCourseId

                        return (
                            <div key={course.id} className="mb-1">
                                <div
                                    onClick={() => navigate(`/learn/course/${course.id}`)}
                                    className={classNames(
                                        "group flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-100",
                                        { "bg-gray-100": isActive }
                                    )}
                                >
                                    <div className={classNames("shrink-0 transition-colors", {
                                        "text-primary-600": isActive,
                                        "text-gray-400 group-hover:text-primary-600": !isActive
                                    })}>
                                        <BookOpen className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className={classNames("text-sm font-semibold truncate transition-colors", {
                                            "text-primary-700": isActive,
                                            "text-gray-800 group-hover:text-primary-600": !isActive
                                        })}>
                                            {course.title}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                                            <span className="flex items-center gap-1">
                                                <PlayCircle className="w-3 h-3" />
                                                {course.lessonCount} 강의
                                            </span>
                                        </div>
                                    </div>
                                    {isActive ? (
                                        <ChevronDown className="w-4 h-4 text-primary-500" />
                                    ) : (
                                        <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-primary-500" />
                                    )}
                                </div>

                                {/* Expanded Course Content */}
                                {isActive && (
                                    <div className="border-t border-gray-200">
                                        <CourseContent
                                            courseId={course.id}
                                            currentLessonId={activeLessonId}
                                        />
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
